// src/pages/SmartSearch/SmartSearchPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Pagination,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle,
  Info,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { propertiesAPI } from '../../services/api';
import PropertyCard from '../../components/PropertyCard/PropertyCard';
import SmartSearchBar from '../../components/SmartSearch/SmartSearchBar';

const SmartSearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [parsedFilters, setParsedFilters] = useState(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');

  useEffect(() => {
    const query = searchParams.get('q');
    if (query) {
      setSearchQuery(query);
      performSearch(query, currentPage);
    }
  }, [searchParams, currentPage]);

  const performSearch = async (query, page = 1) => {
    if (!query.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await propertiesAPI.smartSearch(query, { page });
      
      setProperties(response.data.results || []);
      setTotalPages(Math.ceil((response.data.count || 0) / 20));
      setParsedFilters(response.data.parsed_filters || null);
    } catch (error) {
      console.error('Erro na busca:', error);
      setError('Erro ao realizar a busca. Tente novamente com termos diferentes.');
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewSearch = (query) => {
    setSearchParams({ q: query });
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFavoriteToggle = (propertyId) => {
    setProperties(prev => 
      prev.map(property => 
        property.id === propertyId 
          ? { ...property, is_favorited: !property.is_favorited }
          : property
      )
    );
  };

  const renderParsedFilters = () => {
    if (!parsedFilters) return null;

    const filters = [];

    if (parsedFilters.property_type) {
      filters.push(
        <Chip 
          key="type"
          icon={<CheckCircle />}
          label={`Tipo: ${parsedFilters.property_type}`}
          color="primary"
          variant="outlined"
        />
      );
    }

    if (parsedFilters.transaction_type) {
      filters.push(
        <Chip 
          key="transaction"
          icon={<CheckCircle />}
          label={`${parsedFilters.transaction_type === 'venda' ? 'Venda' : 'Aluguel'}`}
          color="primary"
          variant="outlined"
        />
      );
    }

    if (parsedFilters.bedrooms) {
      filters.push(
        <Chip 
          key="bedrooms"
          icon={<CheckCircle />}
          label={`${parsedFilters.bedrooms} quartos`}
          color="primary"
          variant="outlined"
        />
      );
    }

    if (parsedFilters.bathrooms) {
      filters.push(
        <Chip 
          key="bathrooms"
          icon={<CheckCircle />}
          label={`${parsedFilters.bathrooms} banheiros`}
          color="primary"
          variant="outlined"
        />
      );
    }

    if (parsedFilters.garage_spaces) {
      filters.push(
        <Chip 
          key="garage"
          icon={<CheckCircle />}
          label={`${parsedFilters.garage_spaces} vagas`}
          color="primary"
          variant="outlined"
        />
      );
    }

    if (parsedFilters.max_price) {
      const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
      }).format(parsedFilters.max_price);
      
      filters.push(
        <Chip 
          key="price"
          icon={<CheckCircle />}
          label={`Até ${formattedPrice}`}
          color="primary"
          variant="outlined"
        />
      );
    }

    if (parsedFilters.city) {
      filters.push(
        <Chip 
          key="city"
          icon={<CheckCircle />}
          label={`${parsedFilters.city}`}
          color="primary"
          variant="outlined"
        />
      );
    }

    // Características booleanas
    const booleanFeatures = [
      { key: 'has_pool', label: 'Com piscina' },
      { key: 'has_garden', label: 'Com jardim' },
      { key: 'has_elevator', label: 'Com elevador' },
      { key: 'is_furnished', label: 'Mobiliado' },
    ];

    booleanFeatures.forEach(({ key, label }) => {
      if (parsedFilters[key]) {
        filters.push(
          <Chip 
            key={key}
            icon={<CheckCircle />}
            label={label}
            color="secondary"
            variant="outlined"
          />
        );
      }
    });

    return filters;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Busca Inteligente
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Digite naturalmente o que você procura, como: "Apartamento 3 quartos em Copacabana até R$ 800 mil"
        </Typography>

        {/* Barra de Busca */}
        <SmartSearchBar
          onSearch={handleNewSearch}
          placeholder="Descreva o imóvel que você procura..."
          autoFocus={!searchQuery}
        />
      </Box>

      {/* Filtros Identificados */}
      {parsedFilters && (
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Info color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              Filtros Identificados
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {renderParsedFilters()}
          </Box>
        </Paper>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Resultados */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {loading ? (
            'Buscando imóveis...'
          ) : (
            <>
              {properties.length > 0 ? (
                <>
                  Encontramos <strong>{properties.length}</strong> imóveis para: <strong>"{searchQuery}"</strong>
                </>
              ) : searchQuery ? (
                'Nenhum imóvel encontrado'
              ) : (
                'Digite uma busca para começar'
              )}
            </>
          )}
        </Typography>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Lista de Imóveis */}
      {!loading && !error && properties.length > 0 && (
        <>
          <Grid container spacing={3}>
            {properties.map((property) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={property.id}>
                <PropertyCard 
                  property={property} 
                  onFavoriteToggle={handleFavoriteToggle}
                />
              </Grid>
            ))}
          </Grid>

          {/* Paginação */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* Empty State */}
      {!loading && !error && searchQuery && properties.length === 0 && (
        <Paper sx={{ p: 8, textAlign: 'center' }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nenhum imóvel encontrado
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tente ajustar sua busca ou usar termos diferentes
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default SmartSearchPage;